import { Card, CardContent, makeStyles, LinearProgress, CircularProgress } from "@material-ui/core";
import { AccessTime, TrendingUp, EmojiEvents } from "@material-ui/icons";

const useStyles = makeStyles({
  container: {
    marginBottom: "30px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: 600,
    letterSpacing: "-0.4px",
    margin: 0,
    marginRight: "12px",
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
    "@media (max-width: 1024px)": {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  card: {
    borderRadius: 17,
    padding: "20px 24px",
    border: "1px solid #CCCCCC",
    boxShadow: "none",
    backgroundColor: "#FFFFFF",
  },
  highlightCard: {
    borderRadius: 17,
    padding: "20px 24px",
    border: "1px solid #28a382",
    boxShadow: "none",
    backgroundColor: "#f0fdf9",
  },
  cardContent: {
    padding: "0px !important",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
  },
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#28a382",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    marginRight: "12px",
  },
  icon: {
    color: "#FFFFFF",
    fontSize: "20px",
  },
  cardLabel: {
    fontSize: "14px",
    fontWeight: 400,
    color: "#707070",
    margin: 0,
  },
  mainValue: {
    fontSize: "32px",
    fontWeight: 600,
    color: "#313131",
    margin: "8px 0 4px 0",
    letterSpacing: "-0.5px",
  },
  subValue: {
    fontSize: "14px",
    fontWeight: 400,
    color: "#707070",
    margin: 0,
  },
  featureList: {
    marginTop: "16px",
  },
  featureItem: {
    marginBottom: "12px",
  },
  featureHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "4px",
  },
  featureName: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#313131",
    margin: 0,
  },
  featureValue: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#28a382",
    margin: 0,
  },
  progressBar: {
    height: "6px",
    borderRadius: "3px",
    backgroundColor: "#E5E5E5",
  },
  progressBarFill: {
    backgroundColor: "#28a382",
    borderRadius: "3px",
  },
  rankBadge: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "#FEF3C7",
    color: "#D97706",
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: 500,
    marginTop: "8px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px",
  },
  errorText: {
    color: "#707070",
    fontSize: "14px",
    textAlign: "center",
    padding: "20px",
  },
});

const formatFeatureName = (name) => {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const AnalyticsMetrics = ({ analyticsData, analyticsLoading, analyticsError }) => {
  const classes = useStyles();

  if (analyticsLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.sectionHeader}>
          <h3 className={classes.sectionTitle}>Your Impact</h3>
        </div>
        <div className={classes.loadingContainer}>
          <CircularProgress style={{ color: "#28a382" }} />
        </div>
      </div>
    );
  }

  if (analyticsError) {
    console.error("Analytics error:", analyticsError);
    return (
      <div className={classes.container}>
        <div className={classes.sectionHeader}>
          <h3 className={classes.sectionTitle}>Your Impact</h3>
        </div>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <p className={classes.errorText}>
              Unable to load analytics data. Please try again later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!analyticsData) {
    return null;
  }

  const userComparison = analyticsData.user_comparison;
  console.log("Analytics data received:", analyticsData);

  if (!userComparison) {
    console.log("No user_comparison in analytics data");
    return (
      <div className={classes.container}>
        <div className={classes.sectionHeader}>
          <h3 className={classes.sectionTitle}>Your Impact</h3>
        </div>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <p className={classes.errorText}>
              No usage data available yet. Start using FastRealty features to see your impact!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const timeSaved = userComparison.time_saved;
  const featureBreakdown = userComparison.feature_breakdown || {};
  const systemAverages = userComparison.system_feature_averages || {};

  const getPerformanceLabel = (percent) => {
    if (percent >= 150) return "Power User";
    if (percent >= 100) return "Above Average";
    if (percent >= 75) return "On Track";
    return "Getting Started";
  };

  const topFeatures = Object.entries(featureBreakdown)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  return (
    <div className={classes.container}>
      <div className={classes.sectionHeader}>
        <h3 className={classes.sectionTitle}>Your Impact</h3>
      </div>

      <div className={classes.metricsGrid}>
        {/* Time Saved Card */}
        <Card className={classes.highlightCard}>
          <CardContent className={classes.cardContent}>
            <div className={classes.cardHeader}>
              <div className={classes.iconWrapper}>
                <AccessTime className={classes.icon} />
              </div>
              <p className={classes.cardLabel}>Time Saved</p>
            </div>
            <p className={classes.mainValue}>
              {timeSaved.total_hours_saved.toFixed(1)} hrs
            </p>
            <p className={classes.subValue}>
              {timeSaved.total_minutes_saved} minutes saved this month
            </p>
          </CardContent>
        </Card>

        {/* Usage Stats Card */}
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <div className={classes.cardHeader}>
              <div className={classes.iconWrapper}>
                <TrendingUp className={classes.icon} />
              </div>
              <p className={classes.cardLabel}>Your Usage</p>
            </div>
            <p className={classes.mainValue}>
              {userComparison.user_total_invocations}
            </p>
            <p className={classes.subValue}>
              {userComparison.user_vs_average_percent.toFixed(0)}% of system average
            </p>
            <span className={classes.rankBadge}>
              {getPerformanceLabel(userComparison.user_vs_average_percent)}
            </span>
          </CardContent>
        </Card>

        {/* Ranking Card */}
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <div className={classes.cardHeader}>
              <div className={classes.iconWrapper}>
                <EmojiEvents className={classes.icon} />
              </div>
              <p className={classes.cardLabel}>Your Ranking</p>
            </div>
            <p className={classes.mainValue}>
              #{userComparison.user_rank}
            </p>
            <p className={classes.subValue}>
              out of {userComparison.total_users} users
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Feature Breakdown */}
      {topFeatures.length > 0 && (
        <Card className={classes.card} style={{ marginTop: "16px" }}>
          <CardContent className={classes.cardContent}>
            <p className={classes.cardLabel} style={{ marginBottom: "12px" }}>
              Feature Usage Breakdown
            </p>
            <div className={classes.featureList}>
              {topFeatures.map(([feature, usage]) => {
                const systemAvg = systemAverages[feature] || 1;
                const percent = Math.min((usage / systemAvg) * 100, 100);
                const timeSavedForFeature = timeSaved.breakdown_by_feature?.[feature] || 0;

                return (
                  <div key={feature} className={classes.featureItem}>
                    <div className={classes.featureHeader}>
                      <p className={classes.featureName}>
                        {formatFeatureName(feature)}
                      </p>
                      <p className={classes.featureValue}>
                        {usage} uses ({Math.round(timeSavedForFeature / 60 * 10) / 10} hrs saved)
                      </p>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={percent}
                      classes={{
                        root: classes.progressBar,
                        bar: classes.progressBarFill,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsMetrics;
